// Google Ads tag for the platform app — configuration and reporting.
//
// Kept free of react-router imports so it can be unit tested (the repo's jest
// setup cannot resolve react-router-dom; see App.test.tsx). The router-bound
// route tracker lives in googleAds.tsx.
//
// Conversion IDs and labels are public by design — they appear in the page
// source of every advertiser using gtag, and grant no account access. They are
// hardcoded like the PostHog key and Reddit pixel ID, since the production
// Docker build injects no env vars. Leave GOOGLE_ADS_ID empty to disable.
//
// Ads land on usekestrel.ai but signup happens here, on
// platform.usekestrel.ai. Both hosts must load the tag with the same linker
// domains or gtag treats the hop as a new session and the ad click never gets
// credited for the trial.
export const GOOGLE_ADS_ID = 'AW-18414388857';

/**
 * Conversion label for the "Trial signup" action (conversion type ID 7736589803),
 * from that action's event snippet in Google Ads.
 *
 * This is the *page load* variant of the snippet: the conversion fires when the
 * customer first reaches /setup, which happens exactly once per new account
 * right after registration succeeds. The click variant Google also offers would
 * mean firing on a CTA click instead — wrong here, since clicking "Start free
 * trial" on the marketing site is not a signup and most clicks never finish one.
 */
export const TRIAL_SIGNUP_SEND_TO: string = 'AW-18414388857/eVHjCOv7i-kcEPmM1cxE';

/**
 * Conversion label for the "Workflow created with connected integration"
 * action — the event Maximize Conversions bids on. A trial signup alone is a
 * weak signal (plenty of people register and leave); a real, non-simulated
 * workflow that touches at least one integration the tenant actually connected
 * is what an activated trial looks like.
 *
 * Fires from the API client after POST /workflows succeeds, so every creation
 * path in the app (builder save, Home chat, library suggestions, diagnosis
 * chat) reports through the same gate. CLI/SDK/MCP creations cannot report:
 * the ad click lives in the browser's gclid cookie, so only the browser can
 * attribute it — fine, since ad traffic converts in the UI.
 *
 * Label from the "Workflow created (connected integration)" action's event
 * snippet in Google Ads. The page-load and click variants of that snippet carry
 * the same label; the click variant only adds a navigation callback, which
 * does not apply here since we fire programmatically once the save succeeds.
 */
export const WORKFLOW_CREATED_SEND_TO: string = 'AW-18414388857/-UYcCICrvpIdEPmM1cxE';

const LINKER_DOMAINS = ['usekestrel.ai', 'platform.usekestrel.ai'];

/**
 * Marks a trial signup as already reported. Session-scoped rather than a ref
 * because the customer lands on /setup once per signup but may revisit it (or
 * reload) while finishing onboarding, and none of those may count again.
 */
export const TRIAL_REPORTED_KEY = 'kestrel_trial_conversion_sent';

/**
 * Marks the workflow-created conversion as reported. Browser-scoped (localStorage)
 * rather than session-scoped: the trial conversion only needs to survive the
 * onboarding session, but a customer creates workflows across many sessions
 * and this must count once per converting browser, not once per login.
 * Google's "One per click" counting is the backstop for the cases storage
 * can't cover (cleared storage, second browser).
 */
export const WORKFLOW_CREATED_REPORTED_KEY = 'kestrel_workflow_conversion_sent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Loads gtag.js and configures cross-domain linking. No-op when unconfigured. */
export function initGoogleAds() {
  if (!GOOGLE_ADS_ID || typeof window === 'undefined' || window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  // gtag.js recognises a command only when the pushed item is a raw `arguments`
  // object; a normalized array is silently ignored, which drops `js`, `config`
  // and every conversion event. This mirrors Google's official snippet exactly:
  //   function gtag(){dataLayer.push(arguments);}
  const gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as unknown as NonNullable<Window['gtag']>;
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GOOGLE_ADS_ID, {
    linker: { domains: LINKER_DOMAINS, decorate_forms: true },
  });

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

/** Whether the trial conversion has already been reported this session. */
function trialAlreadyReported(): boolean {
  try {
    return window.sessionStorage.getItem(TRIAL_REPORTED_KEY) === '1';
  } catch {
    // Private browsing can throw on sessionStorage access; fall back to
    // reporting, since an occasional duplicate beats never converting.
    return false;
  }
}

function markTrialReported() {
  try {
    window.sessionStorage.setItem(TRIAL_REPORTED_KEY, '1');
  } catch {
    /* storage unavailable */
  }
}

/**
 * Reports the trial-signup conversion, at most once per session. Returns
 * whether a conversion was actually sent, which keeps this testable without a
 * live gtag.
 */
export function reportTrialSignupConversion(): boolean {
  if (!GOOGLE_ADS_ID || !TRIAL_SIGNUP_SEND_TO) return false;
  if (typeof window === 'undefined' || !window.gtag) return false;
  if (trialAlreadyReported()) return false;

  window.gtag('event', 'conversion', { send_to: TRIAL_SIGNUP_SEND_TO });
  markTrialReported();
  return true;
}

// ---------------------------------------------------------------------------
// Workflow created with a connected integration
// ---------------------------------------------------------------------------

/**
 * Integration keys that are always "connected" because they are Kestrel itself
 * or workflow plumbing, so a workflow built from only these says nothing about
 * whether the customer connected anything.
 */
const BUILTIN_INTEGRATIONS = new Set([
  'kestrel', 'approval', 'custom', 'request', 'schedule', 'custom_webhook',
  'condition', 'foreach', 'loop',
]);

/** Minimal shape of what we need from a workflow to judge it. */
export interface WorkflowForConversion {
  simulated?: boolean;
  trigger_config?: { source?: string } | null;
  definition?: {
    nodes?: Array<{ type?: string; data?: { integration?: string; source?: string } }>;
  } | null;
}

/** Minimal shape of /workflows/integrations/status entries. */
export interface ConnectedIntegration {
  id: string;
  connected: boolean;
}

/**
 * Every integration key a workflow references: action nodes' integration plus
 * the trigger source (from the node and the trigger_config, which can disagree
 * on hand-edited workflows). Built-in plumbing keys are dropped.
 */
export function workflowIntegrationKeys(workflow: WorkflowForConversion): string[] {
  const keys = new Set<string>();
  for (const node of workflow.definition?.nodes ?? []) {
    const integration = node.data?.integration;
    if (integration) keys.add(integration);
    if (node.type === 'trigger' && node.data?.source) keys.add(node.data.source);
  }
  if (workflow.trigger_config?.source) keys.add(workflow.trigger_config.source);
  return Array.from(keys).filter(k => !BUILTIN_INTEGRATIONS.has(k));
}

/**
 * Whether a saved workflow counts as "created with a connected integration":
 * it is not a simulated workflow (those exist precisely so people with nothing
 * connected can still build), and at least one integration it uses is
 * connected for the tenant.
 *
 * A provider's block families (aws-ops, gcp-security, ...) share one connection
 * with the provider key, and the status endpoint reports both, so a family key
 * counts if either it or its provider is connected.
 */
export function workflowUsesConnectedIntegration(
  workflow: WorkflowForConversion,
  statuses: ConnectedIntegration[],
): boolean {
  if (workflow.simulated) return false;
  const connected = new Set(statuses.filter(s => s.connected).map(s => s.id));
  return workflowIntegrationKeys(workflow).some(key => {
    if (connected.has(key)) return true;
    const provider = key.split('-')[0];
    return (provider === 'aws' || provider === 'gcp') && connected.has(provider);
  });
}

function workflowAlreadyReported(): boolean {
  try {
    return window.localStorage.getItem(WORKFLOW_CREATED_REPORTED_KEY) === '1';
  } catch {
    return false;
  }
}

function markWorkflowReported() {
  try {
    window.localStorage.setItem(WORKFLOW_CREATED_REPORTED_KEY, '1');
  } catch {
    /* storage unavailable */
  }
}

/**
 * Whether reporting could possibly do anything right now. Lets the caller skip
 * the integrations-status fetch when the label is unset, gtag is absent, or
 * this browser has already converted.
 */
export function workflowCreatedConversionArmed(): boolean {
  if (!GOOGLE_ADS_ID || !WORKFLOW_CREATED_SEND_TO) return false;
  if (typeof window === 'undefined' || !window.gtag) return false;
  return !workflowAlreadyReported();
}

/**
 * Reports the workflow-created conversion for a just-saved workflow, at most
 * once per browser, and only when the workflow qualifies. Returns whether a
 * conversion was actually sent.
 */
export function reportWorkflowCreatedConversion(
  workflow: WorkflowForConversion,
  statuses: ConnectedIntegration[],
): boolean {
  if (!workflowCreatedConversionArmed()) return false;
  if (!workflowUsesConnectedIntegration(workflow, statuses)) return false;

  window.gtag!('event', 'conversion', { send_to: WORKFLOW_CREATED_SEND_TO });
  markWorkflowReported();
  return true;
}
